import { pushApi } from '@/api/push';

/**
 * Order notifications on this device.
 *
 * Permission can only be asked for from a user gesture and only ever grants
 * this one browser, so there is no way to enrol staff from the server — every
 * device opts in once, and then repairs itself silently on later visits.
 *
 * There is no service worker in dev or test (vite.config.ts), so this reports
 * unsupported there rather than pretending.
 */

const ENABLED_KEY = 'sg.push.enabled';
const ENDPOINT_KEY = 'sg.push.endpoint';

export interface PushSupport {
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  /** iOS grants push only to an installed PWA, so the ask has to come later. */
  needsInstall: boolean;
}

export function readPushSupport(): PushSupport {
  if (typeof window === 'undefined') {
    return { supported: false, permission: 'unsupported', needsInstall: false };
  }

  const hasApis =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true;
  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return {
    supported: hasApis && (!iOS || standalone),
    permission: hasApis ? Notification.permission : 'unsupported',
    needsInstall: iOS && !standalone,
  };
}

export function isPushEnabled(): boolean {
  try {
    // Order alerts are a safety feature, so a device opts in by default. The
    // browser permission prompt still requires an explicit user click; this
    // flag records intent and lets an already-granted device self-repair.
    return localStorage.getItem(ENABLED_KEY) !== '0';
  } catch {
    return true;
  }
}

function remember(enabled: boolean, endpoint?: string): void {
  try {
    localStorage.setItem(ENABLED_KEY, enabled ? '1' : '0');
    if (endpoint) localStorage.setItem(ENDPOINT_KEY, endpoint);
    else localStorage.removeItem(ENDPOINT_KEY);
  } catch {
    // Private browsing. The subscription still works for this session.
  }
}

function applicationServerKey(publicKey: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (publicKey.length % 4)) % 4);
  const binary = atob((publicKey + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const ready = await navigator.serviceWorker.getRegistration();
  if (!ready) throw new Error('Notifications need the app to finish installing. Reload and retry.');
  return ready;
}

/** Must be called from a click: browsers refuse a permission prompt otherwise. */
export async function enablePush(): Promise<void> {
  const support = readPushSupport();
  if (!support.supported) {
    throw new Error(
      support.needsInstall
        ? 'On iPhone, add the console to your home screen first, then turn this on.'
        : 'This browser cannot show notifications.',
    );
  }

  const permission =
    Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notifications were blocked for this site.');

  // Ask while the click still owns browser user activation. A network request
  // before requestPermission can make stricter mobile browsers discard it.
  const { publicKey, available } = await pushApi.key();
  if (!available) throw new Error('Notifications are not configured for this organization yet.');

  const reg = await registration();
  const subscription =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(publicKey),
    }));

  await pushApi.subscribe(subscription.toJSON(), navigator.userAgent);
  remember(true, subscription.endpoint);
}

export async function disablePush(): Promise<void> {
  let endpoint: string | null;
  try {
    endpoint = localStorage.getItem(ENDPOINT_KEY);
  } catch {
    endpoint = null;
  }

  if ('serviceWorker' in navigator) {
    const reg = await navigator.serviceWorker.getRegistration();
    const subscription = await reg?.pushManager.getSubscription();
    if (subscription) {
      endpoint = subscription.endpoint;
      await subscription.unsubscribe().catch(() => undefined);
    }
  }

  // Server first in effect: the row is what decides whether a send happens.
  if (endpoint) await pushApi.unsubscribe(endpoint);
  remember(false);
}

/**
 * Re-attach on later visits without prompting again.
 *
 * Browsers rotate endpoints, and clearing site data drops the subscription
 * while permission survives — both leave a device that believes it is
 * subscribed and never rings.
 */
export async function syncPush(): Promise<void> {
  if (!isPushEnabled()) return;
  const support = readPushSupport();
  if (!support.supported || support.permission !== 'granted') return;

  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return;

    const { publicKey, available } = await pushApi.key();
    if (!available) return;

    const subscription =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey(publicKey),
      }));

    let stored: string | null;
    try {
      stored = localStorage.getItem(ENDPOINT_KEY);
    } catch {
      stored = null;
    }

    // Re-assert even when the endpoint did not rotate: the server row might
    // have been retired, deleted, or assigned to a previously signed-in user.
    await pushApi.subscribe(subscription.toJSON(), navigator.userAgent);
    if (subscription.endpoint !== stored) remember(true, subscription.endpoint);
  } catch {
    // A failed repair must never break the screen that called it.
  }
}
